import type { FlightCardData, NlpFilters, NlpFlightCardData } from '@massilia/queries'

import {
  getApiErrorMessage,
  isExpiredSearchSessionError,
  isProviderUnavailableError,
  useCreateBookingSession,
  useDebugAccessQuery,
  useSearchFromQueryMutation,
} from '@massilia/queries'
import { Button, Label, Switch } from '@massilia/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { SessionExpiredError } from '@/components/booking/session-expired-error'
import { DebugProvider } from '@/components/flights/debug-context'
import { FlightCard } from '@/components/flights/flight-card'
// NLP page: FlightFilters / FiltersModal / FiltersButton are tied to `/_booking/vols/resultats` route match — removed to avoid invariant errors.
// import {
//   FiltersSkeleton,
//   FlightFilters,
// } from '@/components/flights/filters'
// import { FiltersButton } from '@/components/flights/filters/filters-button'
// import { FiltersModal } from '@/components/flights/filters/filters-modal'
import { FlightCardSkeleton } from '@/components/flights/flight-card-skeleton'
import { FlightErrorCard } from '@/components/flights/flight-error-card'
import { FlightSearchLoadingModal } from '@/components/flights/flight-search-loading-modal'
import { NoResultsCard } from '@/components/flights/no-results-card'
import { BackToTopButton } from '@/components/ui/back-to-top-button'
import { useAuthStore } from '@/stores/auth.store'

interface PassengerCount {
  adults: number
  babies: number
  infants: number
  seniors: number
  youngsters: number
}

type TripType = 'multi-city' | 'one-way' | 'round-trip'

export const Route = createFileRoute('/_booking/vols-multi/resultats')({
  component: FlightsManyQueryResultsPage,
  validateSearch: z.object({ q: z.string().optional() }),
})

const AIRLINE_NAMES: Record<string, string> = {
  AF: 'Air France',
  SS: 'Corsair',
  TX: 'Air Caraïbes',
  UU: 'Air Austral',
}

const SLOT_LABELS: Record<string, string> = {
  '0-6': '0h–6h',
  '12-18': '12h–18h',
  '18-24': '18h–24h',
  '6-12': '6h–12h',
}

function derivePassengersCount(flight: FlightCardData): PassengerCount {
  return flight.pricing.perPassenger.reduce<PassengerCount>(
    (acc, p) => {
      if (p.type === 'Infant') acc.babies += p.count
      else if (p.type === 'Child') acc.infants += p.count
      else acc.adults += p.count
      return acc
    },
    { adults: 0, babies: 0, infants: 0, seniors: 0, youngsters: 0 }
  )
}

function FlightsManyQueryResultsPage() {
  const navigate = useNavigate()
  const { q: initialQuery } = Route.useSearch()
  const [query, setQuery] = useState(initialQuery ?? '')
  // const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [isSessionExpiredOnCreate, setIsSessionExpiredOnCreate] = useState(false)
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | undefined>()
  const [showDebug, setShowDebug] = useState(false)

  const { isAuthenticated } = useAuthStore()
  const { data: debugAccess } = useDebugAccessQuery(isAuthenticated)
  const { data: results, error, isPending: isLoading, mutate } = useSearchFromQueryMutation()
  const { mutateAsync: createBookingSession } = useCreateBookingSession()
  const isFlightMaintenance = isProviderUnavailableError(error)
  const flightErrorMessage = isFlightMaintenance ? undefined : getApiErrorMessage(error)
  const isFlightRateLimited = error instanceof AxiosError && error.response?.status === 429

  // Auto-trigger search when the page is opened from the chatbot with a query param.
  useEffect(() => {
    const nextQuery = (initialQuery ?? '').trim()
    setQuery(nextQuery)
    if (nextQuery.length > 0) {
      mutate(nextQuery)
    }
  }, [initialQuery, mutate])

  const handleSelectFlight = useCallback(
    async (flight: FlightCardData, offerTransactionKey: string, selectedJourneyKeys: string[]) => {
      const origin = flight.outbound.departureCity
      const destination = (flight.return?.arrivalCity ?? flight.outbound.arrivalCity) || ''
      const departureDate = flight.outbound.departureDate
      const returnDate = flight.return?.departureDate
      const tripType: TripType = flight.return ? 'round-trip' : 'one-way'

      try {
        const session = await createBookingSession({
          accountCode: flight.pricing.accountCode,
          departureDate,
          destination,
          journeyKeys: selectedJourneyKeys,
          offerKey: flight.offerKey,
          origin,
          passengersCount: derivePassengersCount(flight),
          returnDate,
          transactionKey: offerTransactionKey,
          tripType,
        })

        void navigate({
          search: { session: session.sessionId },
          to: '/informations',
        })
      } catch (err) {
        if (isExpiredSearchSessionError(err)) {
          setSessionExpiredMessage(getApiErrorMessage(err))
          setIsSessionExpiredOnCreate(true)
          return
        }
        throw err
      }
    },
    [createBookingSession, navigate]
  )

  const sortedOffers = useMemo(() => {
    if (!results?.offers || results.offers.length === 0) return []
    return [...results.offers].sort((a, b) => {
      const aHasAccountCode = a.pricing.accountCode !== undefined
      const bHasAccountCode = b.pricing.accountCode !== undefined
      if (aHasAccountCode && !bHasAccountCode) return -1
      if (!aHasAccountCode && bHasAccountCode) return 1
      const priceA = parseFloat(a.pricing.total.amount || '0')
      const priceB = parseFloat(b.pricing.total.amount || '0')
      return priceA - priceB
    })
  }, [results?.offers])
  const sortedOffersWithNlpContext = sortedOffers as NlpFlightCardData[]
  const appliedFilterBadges = useMemo(() => getFilterBadges(results?.appliedFilters), [results])

  // const activeFiltersCount = useMemo(() => {
  //   if (!results?.offers) return 0
  //   let count = 0
  //   if (filterState.excludedAirlines.length > 0) count++
  //   if (filterState.stops !== 'all') count++
  //   if (filterState.luggage !== '1') count++
  //   if (filterState.priceRange) count++
  //   if (filterState.durationRange) count++
  //   if (filterState.timeFilters.length > 0) count++
  //   return count
  // }, [filterState, results?.offers])

  return (
    <DebugProvider value={showDebug}>
      <div className="min-h-screen w-screen bg-blue-50">
        <div className="flex-1 border-b border-gray-200 bg-white py-4 shadow-sm">
          <div className="flex flex-col items-center gap-6 px-4 md:flex-row md:items-center md:px-20">
            {debugAccess?.allowed && (
              <div className="hidden flex-row items-center gap-2 md:flex">
                <Label>Debug</Label>
                <Switch
                  className={`text-xs hover:cursor-pointer ${showDebug ? 'font-semibold text-orange-500' : 'text-gray-400'}`}
                  onCheckedChange={(checked) => {
                    setShowDebug(checked)
                  }}
                >
                  Debug
                </Switch>
              </div>
            )}
            <div className="flex w-full flex-1 flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Rechercher en langage naturel
              </label>
              <textarea
                className="w-full rounded-md border border-gray-300 p-3 text-sm"
                onChange={(e) => {
                  setQuery(e.target.value)
                }}
                placeholder="Ex: Je veux un vol Paris → La Réunion entre le 10 et le 15 avril et retour entre le 20 et le 25 avril, 2 adultes, eco, bagage."
                rows={3}
                value={query}
              />
              <div className="flex justify-end">
                <button
                  className="rounded-md bg-[#1D95AD] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isLoading || query.trim().length === 0}
                  onClick={() => {
                    mutate(query.trim())
                  }}
                  type="button"
                >
                  Lancer la recherche
                </button>
              </div>
              {!initialQuery?.trim() && !isLoading && !results && !error && (
                <p className="text-xs text-gray-500">
                  Aucune requête fournie via l&apos;URL. Saisissez une recherche ou lancez-la depuis le chat.
                </p>
              )}
            </div>
          </div>
        </div>

        {isSessionExpiredOnCreate ? (
          <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
            <SessionExpiredError
              message={sessionExpiredMessage}
              onAction={() => {
                void navigate({ to: '/recherche' })
              }}
            />
          </div>
        ) : (
          <>
            {results?.offers.length === 0 && (
              <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                <NoResultsCard
                  departureLabel="Départ"
                  destinationLabel="Destination"
                  onRetryWithoutLuggage={() => {
                    mutate(query.trim())
                  }}
                  withLuggage={false}
                />
              </div>
            )}

            {error && (
              <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                {isFlightMaintenance ? (
                  <FlightErrorCard
                    onRetry={() => {
                      mutate(query.trim())
                    }}
                    type="maintenance"
                  />
                ) : isFlightRateLimited || flightErrorMessage ? (
                  <div className="flex w-full max-w-md flex-col items-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                      <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h1 className="text-gray-light-900 mt-6 text-xl font-semibold">
                      {isFlightRateLimited ? 'Trop de requêtes' : 'Erreur de recherche'}
                    </h1>
                    <div className="border-gray-light-200 bg-gray-light-50 mt-6 w-full rounded-xl border px-5 py-4">
                      <p className="text-gray-light-700 text-sm leading-relaxed">
                        {isFlightRateLimited
                          ? 'Vous avez effectué trop de recherches en peu de temps. Merci de patienter une minute avant de réessayer.'
                          : flightErrorMessage}
                      </p>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                      <Button
                        className="bg-brand-600 hover:bg-brand-700"
                        onClick={() => {
                          mutate(query.trim())
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                        Réessayer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <FlightErrorCard
                    onRetry={() => {
                      mutate(query.trim())
                    }}
                    type="error"
                  />
                )}
              </div>
            )}

            {(isLoading || (results && results.offers.length > 0)) && (
              <div className="w-full flex-1 gap-4 px-4 py-4 md:px-20 md:py-8">
                {/*
                <div className="w-full flex-1 gap-4 px-4 py-4 md:grid md:grid-cols-[1fr_3fr] md:gap-6 md:px-20 md:py-8">
                  {!isMobile && (
                    <aside className="h-fit min-w-0">
                      {results && results.offers.length > 0 && (
                        <FlightFilters offers={results.offers} />
                      )}
                      {isLoading && <FiltersSkeleton />}
                    </aside>
                  )}
                </div>
                */}

                <main className="min-w-0">
                  <FlightSearchLoadingModal
                    onCancel={() => {
                      void navigate({ to: '/recherche' })
                    }}
                    open={isLoading}
                  />

                  {isLoading && (
                    <div className="w-full space-y-4">
                      <div className="mb-4 h-5 w-32 animate-pulse rounded bg-gray-200" />
                      {Array.from({ length: 4 }).map((_, i) => (
                        <FlightCardSkeleton key={i} />
                      ))}
                    </div>
                  )}

                  {results && results.offers.length > 0 && (
                    <>
                      {/*
                      {isMobile && (
                        <FiltersButton
                          activeFiltersCount={activeFiltersCount}
                          onClick={() => {
                            setIsFiltersModalOpen(true)
                          }}
                        />
                      )}
                      */}

                      <div className="mb-4 text-sm text-gray-600">
                        {sortedOffers.length} vol{sortedOffers.length > 1 ? 's' : ''} trouvé
                        {sortedOffers.length > 1 ? 's' : ''}
                      </div>

                      {appliedFilterBadges.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                          {appliedFilterBadges.map((badge) => (
                            <span
                              className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700"
                              key={badge}
                            >
                              {badge}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="space-y-4">
                        {sortedOffersWithNlpContext.map((offer) => (
                          <div
                            className="flex flex-col"
                            key={`${getSafeTransactionKey(offer, results.transactionKey)}__${offer.offerId}`}
                          >
                            <div className="rounded-t-lg border border-b-0 border-sky-200 bg-sky-50 px-4 py-2 text-xs font-medium text-sky-700">
                              Aller: {offer.outbound.departureDate}
                              {offer.return ? ` · Retour: ${offer.return.departureDate}` : ''}
                            </div>
                            <FlightCard
                              flight={offer}
                              onSelect={(selectedFlight, selectedJourneyKeys) => {
                                void handleSelectFlight(
                                  selectedFlight,
                                  getSafeTransactionKey(offer, results.transactionKey),
                                  selectedJourneyKeys
                                )
                              }}
                              transactionKey={getSafeTransactionKey(offer, results.transactionKey)}
                            />
                          </div>
                        ))}
                      </div>

                      {/*
                      {isMobile && (
                        <FiltersModal
                          isOpen={isFiltersModalOpen}
                          offers={results.offers}
                          onClose={() => {
                            setIsFiltersModalOpen(false)
                          }}
                        />
                      )}
                      */}
                    </>
                  )}
                </main>
              </div>
            )}
          </>
        )}

        <BackToTopButton />
      </div>
    </DebugProvider>
  )
}

function getFilterBadges(filters?: NlpFilters): string[] {
  if (!filters) return []

  const badges: string[] = []

  switch (filters.stops) {
    case 'direct':
      badges.push('Vol direct')
      break
    case 'one_stop_max':
      badges.push('1 escale max')
      break
    case 'two_stops_max':
      badges.push('2 escales max')
      break
    default:
      break
  }

  if (typeof filters.maxPrice === 'number' && Number.isFinite(filters.maxPrice)) {
    badges.push(`Prix max: ${filters.maxPrice} EUR`)
  }

  if (filters.excludedAirlines && filters.excludedAirlines.length > 0) {
    const names = filters.excludedAirlines.map((code) => AIRLINE_NAMES[code] ?? code)
    badges.push(`Sans: ${names.join(', ')}`)
  }

  if (filters.luggage) {
    switch (filters.luggage) {
      case '0':
        badges.push('0 bagage et +')
        break
      case '1':
        badges.push('1 bagage min')
        break
      case '2':
        badges.push('2 bagages min')
        break
      case 'none':
        badges.push('Sans bagage')
        break
    }
  }

  if (
    typeof filters.maxDurationMinutes === 'number' &&
    Number.isFinite(filters.maxDurationMinutes)
  ) {
    const h = Math.floor(filters.maxDurationMinutes / 60)
    const m = filters.maxDurationMinutes % 60
    badges.push(`Durée max: ${h}h${m > 0 ? `${String(m).padStart(2, '0')}` : ''}`)
  }

  if (filters.timeFilters && filters.timeFilters.length > 0) {
    for (const tf of filters.timeFilters) {
      const label = `Vol ${tf.segmentIndex + 1}`
      const dep = tf.departureTimeSlots ?? []
      const arr = tf.arrivalTimeSlots ?? []
      if (dep.length > 0) {
        badges.push(`${label} départ: ${dep.map((s) => SLOT_LABELS[s] ?? s).join(', ')}`)
      }
      if (arr.length > 0) {
        badges.push(`${label} arrivée: ${arr.map((s) => SLOT_LABELS[s] ?? s).join(', ')}`)
      }
    }
  }

  return badges
}

function getSafeTransactionKey(
  offer: NlpFlightCardData,
  fallbackTransactionKey: string | undefined
): string {
  if (typeof offer.nlpTransactionKey === 'string' && offer.nlpTransactionKey.length > 0) {
    return offer.nlpTransactionKey
  }
  if (typeof fallbackTransactionKey === 'string' && fallbackTransactionKey.length > 0) {
    return fallbackTransactionKey
  }
  return ''
}
